import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { Briefcase, ChevronDown, Plus, Search, X } from 'lucide-react';

import useProjectStore from '../../stores/projectStore';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { getProjectDisplayName } from '../../lib/utils';
import { isClassicShellRoute } from '../../lib/routes';
import { NewProjectForm, Project, StructuredAddress } from '../../types';
import AddressAutocomplete from '../AddressAutocomplete';

// ================================================================= //
//          ProjectContextHeader                                     //
// ================================================================= //
// Persistent sticky strip rendered below the global nav on every    //
// page of the authenticated app. Shows the currently-selected       //
// project (or a "no project" empty state) with controls to switch / //
// select / create. URL (`?project=<id>`) is the source of truth;    //
// the projectStore mirrors it. Switching projects navigates to the  //
// new `?project=<id>` so deep-links keep working.                   //
// ================================================================= //

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

type FlyoutMode = 'closed' | 'switch' | 'create';

/**
 * Build a URL string that preserves the current pathname and query but
 * sets/clears the `?project=<id>` parameter.
 */
function buildProjectUrl(
  pathname: string,
  search: string,
  projectId: string | null,
): string {
  const params = new URLSearchParams(search);
  if (projectId) {
    params.set('project', projectId);
  } else {
    params.delete('project');
  }
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export default function ProjectContextHeader() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Reason: project id can come from either `?project=<id>` (used by
  // ScopeBuilder, Completeness, etc.) or `/project/:id` (the
  // ProjectDetailPage route). Honor both so the header doesn't clear
  // selectedProject on routes that pass the id as a path param.
  const detailRouteMatch = useMatch('/project/:id');
  const detailRouteProjectId = detailRouteMatch?.params.id ?? null;
  const projectIdParam = searchParams.get('project') ?? detailRouteProjectId;

  // Admin + rule screens render in the classic shell and have no project
  // context — hide the strip there (and skip the URL-mirroring effect so it
  // doesn't append `?project=` to an admin URL). See lib/routes.
  const hidden = isClassicShellRoute(location.pathname);

  const {
    selectedProject,
    selectedProjectId,
    projects,
    setSelectedProject,
    getProjectById,
    fetchProjects,
  } = useProjectStore();

  const [mode, setMode] = useState<FlyoutMode>('closed');

  // Reason: URL is source of truth, but the active project is sticky across
  // tabs/reloads. When `?project=<id>` is present we fetch + mirror it. When
  // it's absent we DON'T clear if we have a persisted selection — instead we
  // rewrite the URL to carry it, so sidebar links (which drop the param) and
  // full reloads keep the project selected. Only clear when nothing is
  // persisted either.
  useEffect(() => {
    if (hidden) return;
    let cancelled = false;
    if (!projectIdParam) {
      if (selectedProjectId) {
        navigate(
          buildProjectUrl(location.pathname, location.search, selectedProjectId),
          { replace: true },
        );
      } else if (selectedProject) {
        setSelectedProject(null);
      }
      return;
    }
    if (selectedProject?.id === projectIdParam) return;
    getProjectById(projectIdParam).then((p) => {
      if (cancelled) return;
      if (p) setSelectedProject(p);
    });
    return () => {
      cancelled = true;
    };
  }, [
    projectIdParam,
    selectedProject,
    selectedProjectId,
    getProjectById,
    setSelectedProject,
    navigate,
    location.pathname,
    location.search,
    hidden,
  ]);

  // Reason: Switch flyout needs the project list; fetch lazily when first opened.
  useEffect(() => {
    if (mode === 'switch' && projects.length === 0) {
      fetchProjects();
    }
  }, [mode, projects.length, fetchProjects]);

  const handleSelectProject = (id: string) => {
    setMode('closed');
    // Reason: when on the /project/:id detail route, switching must rewrite
    // the path so ProjectDetailPage re-mounts on the new id. Other routes
    // use the ?project= query param as before.
    if (detailRouteMatch) {
      navigate(`/project/${id}${location.search}`);
    } else {
      navigate(buildProjectUrl(location.pathname, location.search, id));
    }
  };

  const handleProjectCreated = (project: Project) => {
    setMode('closed');
    setSelectedProject(project);
    if (detailRouteMatch) {
      navigate(`/project/${project.id}${location.search}`);
    } else {
      navigate(buildProjectUrl(location.pathname, location.search, project.id));
    }
  };

  const subtitle = selectedProject
    ? [selectedProject.address, selectedProject.claim_number]
        .filter((v): v is string => Boolean(v))
        .join(' · ')
    : null;

  if (hidden) return null;

  return (
    <div
      data-testid="project-context-header"
      className="sticky top-0 z-30 flex flex-wrap items-center justify-between gap-2 border-b border-border bg-white/95 px-4 py-2 backdrop-blur md:px-6"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Briefcase className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        {selectedProject ? (
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-semibold text-foreground">
              Project: {getProjectDisplayName(selectedProject)}
            </span>
            {subtitle && (
              <span className="truncate text-xs text-muted-foreground">
                {subtitle}
              </span>
            )}
          </div>
        ) : (
          <span className="text-sm font-medium text-muted-foreground">
            No project selected
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {selectedProject ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMode('switch')}
            aria-label="Switch project"
          >
            Switch Project
            <ChevronDown className="ml-1 h-3.5 w-3.5" />
          </Button>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setMode('switch')}
            >
              Select existing
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setMode('create')}
            >
              <Plus className="-ml-0.5 mr-1 h-3.5 w-3.5" />
              Create new project
            </Button>
          </>
        )}
      </div>

      {mode === 'switch' && (
        <ProjectSwitcherModal
          onClose={() => setMode('closed')}
          onSelect={handleSelectProject}
          onCreateNew={() => setMode('create')}
        />
      )}
      {mode === 'create' && (
        <CreateProjectModal
          onClose={() => setMode('closed')}
          onCreated={handleProjectCreated}
        />
      )}
    </div>
  );
}

// ================================================================= //
//          Switcher modal — list/search existing projects           //
// ================================================================= //

interface ProjectSwitcherModalProps {
  onClose: () => void;
  onSelect: (id: string) => void;
  onCreateNew: () => void;
}

const ProjectSwitcherModal: React.FC<ProjectSwitcherModalProps> = ({
  onClose,
  onSelect,
  onCreateNew,
}) => {
  const { projects, loading } = useProjectStore();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = projects.filter((p) => p.status !== 'archived');
    if (!q) return active.slice(0, 50);
    return active
      .filter(
        (p) =>
          (p.owner_name || '').toLowerCase().includes(q) ||
          (p.address || '').toLowerCase().includes(q) ||
          (p.claim_number || '').toLowerCase().includes(q),
      )
      .slice(0, 50);
  }, [projects, query]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Switch project"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-20"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b pb-3">
          <div>
            <h4 className="text-base font-semibold text-foreground">
              Select a project
            </h4>
            <p className="mt-1 text-xs text-muted-foreground">
              Switching navigates to the same page with the new project.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative mt-3">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            autoFocus
            placeholder="Search by owner, address, or claim #"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
            aria-label="Search projects"
          />
        </div>

        <div className="mt-3 max-h-80 overflow-y-auto">
          {loading && projects.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Loading projects...
            </p>
          ) : filtered.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <p>No projects match.</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={onCreateNew}
                className="mt-2"
              >
                <Plus className="-ml-0.5 mr-1 h-3.5 w-3.5" />
                Create new project
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(p.id)}
                    className="flex w-full flex-col items-start gap-0.5 rounded px-2 py-2 text-left hover:bg-slate-50"
                  >
                    <span className="text-sm font-medium text-foreground">
                      {getProjectDisplayName(p)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {[p.address, p.claim_number]
                        .filter(Boolean)
                        .join(' · ') || 'No address'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-3 flex justify-between border-t pt-3">
          <Button variant="ghost" size="sm" onClick={onCreateNew}>
            <Plus className="-ml-0.5 mr-1 h-3.5 w-3.5" />
            Create new project
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

// ================================================================= //
//          Create-project modal — inline form                       //
// ================================================================= //

export interface CreateProjectModalProps {
  onClose: () => void;
  onCreated: (project: Project) => void;
  /** Override the dialog title (default: "Create new project"). */
  title?: string;
  /** Optional subtitle shown under the title. */
  subtitle?: string;
  /** Submit button label override (default: "Create project"). */
  submitLabel?: string;
  /** Optional initial values for the form. */
  initialValues?: Partial<NewProjectForm & {
    policy_number?: string;
    loss_type?: string;
    claim_type?: string;
  }>;
}

interface CreateFormState extends NewProjectForm {
  policy_number?: string;
  loss_type?: string;
  claim_type?: string;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  onClose,
  onCreated,
  title = 'Create new project',
  subtitle = 'This project will be set as active.',
  submitLabel = 'Create project',
  initialValues,
}) => {
  const { createProject } = useProjectStore();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateFormState>({
    street: '',
    city: '',
    state: '',
    zip: '',
    owner_name: '',
    claim_number: '',
    policy_number: '',
    loss_type: '',
    claim_type: '',
    ...initialValues,
  });

  const handleAddressSelect = (addr: StructuredAddress) => {
    setForm((prev) => ({
      ...prev,
      street: addr.street || prev.street,
      city: addr.city || prev.city,
      state: addr.state || prev.state,
      zip: addr.zip || prev.zip,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !form.street.trim() ||
      !form.city.trim() ||
      !form.state ||
      !form.zip.trim()
    ) {
      setError('Street, city, state, and ZIP are required.');
      return;
    }
    setSubmitting(true);
    setError(null);

    // Reason: NewProjectForm only declares a subset of fields. Stash
    // policy/loss/claim type in metadata so we don't lose them, and only
    // send populated optional fields to avoid backend validation noise.
    const payload: NewProjectForm = {
      street: form.street,
      city: form.city,
      state: form.state,
      zip: form.zip,
    };
    if (form.owner_name?.trim()) payload.owner_name = form.owner_name.trim();
    if (form.claim_number?.trim())
      payload.claim_number = form.claim_number.trim();

    try {
      const created = await createProject(payload);
      if (!created) {
        setError('Failed to create project.');
        return;
      }
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create new project"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl rounded-lg border bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2 border-b pb-3">
          <div>
            <h4 className="text-base font-semibold text-foreground">
              {title}
            </h4>
            <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-3 space-y-4">
          <div className="space-y-2">
            <Label>Quick address lookup</Label>
            <AddressAutocomplete
              onSelect={handleAddressSelect}
              placeholder="Start typing an address to auto-fill..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hdr-street">Street Address *</Label>
            <Input
              id="hdr-street"
              value={form.street}
              onChange={(e) =>
                setForm((p) => ({ ...p, street: e.target.value }))
              }
              required
              autoComplete="address-line1"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="hdr-city">City *</Label>
              <Input
                id="hdr-city"
                value={form.city}
                onChange={(e) =>
                  setForm((p) => ({ ...p, city: e.target.value }))
                }
                required
                autoComplete="address-level2"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hdr-state">State *</Label>
              <select
                id="hdr-state"
                value={form.state}
                onChange={(e) =>
                  setForm((p) => ({ ...p, state: e.target.value }))
                }
                required
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Select...</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hdr-zip">ZIP *</Label>
              <Input
                id="hdr-zip"
                value={form.zip}
                onChange={(e) =>
                  setForm((p) => ({ ...p, zip: e.target.value }))
                }
                required
                maxLength={10}
                autoComplete="postal-code"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="hdr-owner">Owner Name</Label>
              <Input
                id="hdr-owner"
                value={form.owner_name || ''}
                onChange={(e) =>
                  setForm((p) => ({ ...p, owner_name: e.target.value }))
                }
                autoComplete="name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hdr-claim">Claim #</Label>
              <Input
                id="hdr-claim"
                value={form.claim_number || ''}
                onChange={(e) =>
                  setForm((p) => ({ ...p, claim_number: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="hdr-policy">Policy #</Label>
              <Input
                id="hdr-policy"
                value={form.policy_number || ''}
                onChange={(e) =>
                  setForm((p) => ({ ...p, policy_number: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hdr-loss">Loss type</Label>
              <select
                id="hdr-loss"
                value={form.loss_type || ''}
                onChange={(e) =>
                  setForm((p) => ({ ...p, loss_type: e.target.value }))
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Select...</option>
                <option value="water">Water</option>
                <option value="fire">Fire</option>
                <option value="wind">Wind</option>
                <option value="hurricane">Hurricane</option>
                <option value="hail">Hail</option>
                <option value="flood">Flood</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="hdr-ctype">Claim type</Label>
              <select
                id="hdr-ctype"
                value={form.claim_type || ''}
                onChange={(e) =>
                  setForm((p) => ({ ...p, claim_type: e.target.value }))
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Select...</option>
                <option value="homeowner">Homeowner</option>
                <option value="commercial">Commercial</option>
                <option value="flood">NFIP / Flood</option>
                <option value="auto">Auto</option>
              </select>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t pt-3">
          <Button
            variant="ghost"
            type="button"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={submitting}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};

